import React, { useEffect, useState } from "react";

import {
  CheckCircleFill,
  Clock,
  ClockNoFill,
  CoinsFill,
  CoinsHand,
  CoinsStacked,
  LockFill,
  PauseCircleFill,
  Pencil as PencilIcon,
  PlayCircle,
  PlayCircleFill,
  PlusCircle,
  StopCircle,
  StopCircleFill,
  User,
} from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import useAnalytics from "@hl/shared-features/lib/features/analytics/useAnalytics";
import ScheduledPricesList from "@hl/shared-features/lib/features/mint/ScheduledPricesList";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import { maskAddress } from "@hl/shared-features/lib/utils/content";
import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import { getMintVectorId } from "@hl/shared-features/lib/utils/getMintVectorId";
import {
  Button,
  CardProps,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
} from "@mantine/core";

import {
  _CollectionType,
  CollectionContractType,
  PriceType,
  TokenDistributionType,
} from "apollo/graphql.generated";
import { ReactComponent as EyeOff } from "assets/icons/eye-off.svg";
import { ReactComponent as Star6 } from "assets/icons/star-06.svg";
import { longFormatDate } from "utils/date";
import { ReactComponent as BezierCurve } from "~/assets/icons/bezier-curve.svg";
import { ReactComponent as Layers } from "~/assets/icons/layers-three.svg";
import { ReactComponent as PieChart } from "~/assets/icons/pie-chart.svg";
import { ManageSale } from "~/features/tokens/sections/ManageToken/MintVector/ManageSale";
import { MintVectorsMenu } from "~/features/tokens/sections/ManageToken/MintVector/MintVectorsMenu";
import { SaleConfig } from "~/features/tokens/sections/ManageToken/SaleStatusBox";
import { PropertyCard } from "~/features/tokens/sections/ManageToken/properties/PropertyCard";
import {
  GetManageTokenCollectionQuery,
  useGetManageTokenGateQuery,
} from "~/features/tokens/sections/ManageToken/queries.graphql.generated";
import {
  getSaleStatus,
  SaleStatus,
} from "~/features/tokens/sections/ManageToken/types";

import { ClaimRankedAuctionEarnings } from "./earnings";

export type MintVectors =
  GetManageTokenCollectionQuery["getCollection"]["mintVectors"];

export type MintVector = MintVectors[0];

export const MultipleMintVector = ({
  collection,
  mintVectors,
  ...rest
}: {
  collection: GetManageTokenCollectionQuery["getCollection"];
  mintVectors: MintVectors;
} & Omit<CardProps, "children">) => {
  const { trackEvent, EventType } = useAnalytics();
  const { pushModal, popModal } = useModalStack();
  const isDeploying = collection.status === "IN_PROGRESS";
  const [mintVector, setMintVector] = useState<MintVector | null>(null);

  useEffect(() => {
    setMintVector(mintVectors?.at(0) || null);
  }, [mintVectors]);

  const { data: gateData, loading: gateDataLoading } =
    useGetManageTokenGateQuery({
      variables: {
        id: mintVector?.gateId || "",
      },
      skip: isDeploying || !mintVector || !mintVector.gateId,
    });

  if (!mintVector) return <></>;

  const endDate = mintVector.end ? new Date(mintVector.end) : undefined;
  const isFixedPriceMintVector =
    !mintVector.priceType || mintVector.priceType === PriceType.Fixed;
  const isRankedAuction = mintVector.priceType === PriceType.RankedAuction;
  const isDutchAuction = mintVector.priceType === PriceType.DutchAuction;

  const saleStatus: SaleStatus = getSaleStatus(mintVector, collection);

  const mintVectorId = getMintVectorId(
    mintVector.isPublic,
    mintVector.id ?? undefined,
    mintVector.onchainMintVectorId ?? undefined
  );

  const isFixedPriceCollection =
    mintVectors.find(
      (mintVector) => mintVector.priceType === PriceType.Fixed
    ) != null;

  const hasAnyPublicSale = mintVectors.some(
    (mintVector) => mintVector.isPublic
  );
  const isPublicSaleEnded =
    hasAnyPublicSale &&
    [SaleStatus.ENDED, SaleStatus.SOLD_OUT].includes(saleStatus);

  const hasMintedTokens = !!mintVector?.stats?.sold;
  const isImported = collection.type === CollectionContractType.Imported;

  const isCollectorsChoiceFixedPriceCollection =
    isFixedPriceCollection &&
    collection.collectionType === _CollectionType.Series &&
    !collection.reveal;

  const earningsInvalid =
    mintVector.priceType === PriceType.DutchAuction &&
    (mintVector.isDirectMint ?? false) &&
    !mintVector.stats?.onchainDutchAuctionStats?.earnedAmountIsValid;

  const currencySymbol = getCurrencySymbol(
    mintVector.chainId || "",
    mintVector.paymentCurrency?.symbol
  );
  return (
    <Paper withBorder={false} shadow="sm" {...rest}>
      <MintVectorsMenu
        mintVectors={mintVectors}
        onSelected={(mintVector) => setMintVector(mintVector)}
        collection={collection}
      />
      <Divider />
      <Stack p={16}>
        <PropertyCard
          withDivider={false}
          icon={<PieChart width={20} height={20} />}
          label="Sales"
          value={
            earningsInvalid
              ? "-"
              : `${mintVector.stats?.earned} ${currencySymbol}`
          }
        />
        <PropertyCard
          icon={<Layers width={20} height={20} />}
          label="Number minted"
          value={mintVector.stats?.sold}
        />
        <PropertyCard
          hidden={!isFixedPriceMintVector}
          icon={<CoinsFill width={20} height={20} />}
          label="Price"
          value={`${mintVector.price} ${currencySymbol}`}
        />

        <PropertyCard
          hidden={!isRankedAuction}
          icon={<CoinsFill width={20} height={20} />}
          label="Minimum bid"
          value={`${mintVector?.rankedAuction?.reserveBidAmount} ${currencySymbol}`}
        />
        <PropertyCard
          hidden={!isRankedAuction}
          icon={<CoinsHand width={20} height={20} />}
          label="Enable rebates for all winning bids"
          value={mintVector?.rankedAuction?.rebateWinningBids ? "Yes" : "No"}
        />
        <PropertyCard
          hidden={!isDutchAuction}
          icon={<CoinsFill width={20} height={20} />}
          label="Price"
          value={<ScheduledPricesList priceDrops={mintVector.priceDrops} />}
        />
        <PropertyCard
          hidden={!isDutchAuction}
          icon={<ClockNoFill width={20} height={20} />}
          label="Price drop interval"
          value={mintVector.priceDropInterval?.map((period) =>
            period.value > 0
              ? ` ${period.value}${period.unit.charAt(0).toLowerCase()}`
              : ""
          )}
        />
        <PropertyCard
          hidden={saleStatus !== SaleStatus.NOT_STARTED}
          icon={<PlayCircle width={20} height={20} />}
          label="Start date"
          value={longFormatDate(new Date(mintVector.start))}
        />
        <PropertyCard
          icon={<StopCircle width={20} height={20} />}
          label="End date"
          value={endDate ? longFormatDate(endDate) : "None"}
        />
        <PropertyCard
          hidden={collection.collectionType !== _CollectionType.Series}
          icon={<Star6 width={20} height={20} />}
          label="Mint style"
          value={collection.reveal ? "Random mint" : "Collector's choice"}
        />
        <PropertyCard
          hidden={collection.collectionType !== _CollectionType.Series}
          icon={<EyeOff width={20} height={20} />}
          label="Hide series metadata"
          value={
            collection.seriesDetails?.showAllMetadata === false ? "Yes" : "No"
          }
        />
        <PropertyCard
          hidden={mintVector.isPublic}
          icon={<LockFill width={20} height={20} />}
          label="Gate"
          value={
            gateDataLoading
              ? "Loading..."
              : gateData && gateData.gate.id
              ? gateData.gate.name
              : "No gate"
          }
        />
        <PropertyCard
          icon={<CoinsHand width={20} height={20} />}
          label="Limit mints per wallet"
          value={mintVector.maxPerUser !== 0 ? "Yes" : "No"}
        />
        <PropertyCard
          hidden={mintVector.maxPerUser === 0}
          icon={<CoinsStacked width={20} height={20} />}
          label="Maximum mints allowed per wallet"
          value={mintVector.maxPerUser.toString()}
        />
        <PropertyCard
          hidden={!mintVector?.paymentRecipient}
          icon={<User width={20} height={20} />}
          label="Payout address"
          value={maskAddress(mintVector.paymentRecipient, 6, 4)}
        />
        <PropertyCard
          icon={<BezierCurve width={20} height={20} />}
          label="Mint vector ID"
          value={
            mintVector.isPublic &&
            mintVector.priceType != PriceType.DutchAuction
              ? mintVectorId
              : maskAddress(mintVectorId, 6, 4)
          }
        />
      </Stack>

      <Divider />
      <Group p="md" spacing="md" position="center" grow>
        <Button
          color={SECONDARY_COLOR}
          disabled={
            isDeploying ||
            (isPublicSaleEnded && isRankedAuction && hasMintedTokens) ||
            isImported
          }
          onClick={() => {
            pushModal(
              <ManageSale
                distributionType={TokenDistributionType.Mint}
                mintVector={mintVector}
                collection={collection}
                onClose={popModal}
                isFixedPriceCollection={isFixedPriceCollection}
                enableGatelessPrivateSales={
                  isCollectorsChoiceFixedPriceCollection
                }
              />,
              {
                size: "lg",
                title: (
                  <Text fw={500} size="lg">
                    Edit sale
                  </Text>
                ),
              }
            );
            trackEvent(EventType.EditToken_EditSaleModalOpened, {
              collectionId: collection.id,
              mintVectorId: mintVector.id,
            });
          }}
          leftIcon={<PencilIcon />}
        >
          Edit sale
        </Button>
        <Button
          disabled={
            isDeploying ||
            isRankedAuction ||
            (!isFixedPriceCollection &&
              !collection.flagVariations.multipleMintVectorsEnableMultipleDA) ||
            isImported
          }
          leftIcon={<PlusCircle />}
          onClick={() =>
            pushModal(
              <ManageSale
                distributionType={TokenDistributionType.Mint}
                mintVector={null}
                collection={collection}
                onClose={popModal}
                isFixedPriceCollection={isFixedPriceCollection}
                disableNewPublicSale={
                  hasAnyPublicSale || isCollectorsChoiceFixedPriceCollection
                }
                enableGatelessPrivateSales={
                  isCollectorsChoiceFixedPriceCollection
                }
              />,
              {
                size: "lg",
                title: (
                  <Text fw={500} size="lg">
                    Create sale
                  </Text>
                ),
              }
            )
          }
        >
          Add sale
        </Button>
      </Group>
      {isRankedAuction &&
        isPublicSaleEnded &&
        mintVector.stats?.earned !== "0.0" &&
        mintVector?.id && (
          <>
            <Divider />
            <ClaimRankedAuctionEarnings
              mintVectorId={mintVector.id}
              collection={collection}
            />
          </>
        )}
    </Paper>
  );
};

export const saleTitleMap: { [key in SaleStatus]: SaleConfig } = {
  [SaleStatus.NOT_STARTED]: {
    mintStatus: "Sale starts :days",
    color: TEXT_COLOR.PRIMARY,
    icon: <Clock width={20} height={20} />,
  },
  [SaleStatus.ACTIVE]: {
    mintStatus: "Sale live",
    color: STATUS_COLOR.AFFIRMATIVE,
    icon: <PlayCircleFill width={20} height={20} />,
  },
  [SaleStatus.ENDED]: {
    mintStatus: "Sale ended",
    color: TEXT_COLOR.SECONDARY,
    icon: <StopCircleFill width={20} height={20} />,
  },
  [SaleStatus.PAUSED]: {
    mintStatus: "Sale paused",
    color: TEXT_COLOR.SECONDARY,
    icon: <PauseCircleFill width={20} height={20} />,
  },
  [SaleStatus.SOLD_OUT]: {
    mintStatus: "Sold out",
    color: TEXT_COLOR.SECONDARY,
    icon: <CheckCircleFill width={20} height={20} />,
  },
  [SaleStatus.NEEDS_FULFILLMENT]: {
    mintStatus: "Needs fulfillment",
    color: TEXT_COLOR.SECONDARY,
    icon: <CheckCircleFill width={20} height={20} />,
  },
  [SaleStatus.RESERVE_NOT_MET]: {
    mintStatus: "Reserve not met",
    color: TEXT_COLOR.SECONDARY,
    icon: <CheckCircleFill width={20} height={20} />,
  },
};
